/**
 * The menu (§14.2): the same ☰ button on every phone. The host finds the public, logged
 * controls there (pause/resume, the current phase's time, end the phase now, upcoming timers,
 * connect a big screen); everyone else finds Request more time and Request pause. The rules
 * badge sits at the top on every phone. The ☰ button shows the public request count.
 */
import { useState } from "react";
import { HoldButton } from "../components/HoldButton.tsx";
import { Sheet } from "../components/Sheet.tsx";
import { navigate } from "../lib/router.ts";
import { forgetSession } from "../lib/session.ts";
import { readLocal, writeLocal } from "../lib/storage.ts";
import { send, toast, useGame } from "../lib/store.ts";
import { useView } from "../lib/view.ts";
import { RulesBadge } from "../components/RulesBadge.tsx";
import { ConnectScreenButton } from "./host/ConnectScreen.tsx";
import { IntroLink } from "../components/IntroLink.tsx";
import { RulesLink } from "../components/RulesLink.tsx";
import { InGameTimers } from "./host/Timers.tsx";

const LARGE_TEXT = "handshake:large-text";

export function applyTextSize(): void {
  document.documentElement.classList.toggle("large-text", readLocal(LARGE_TEXT) === "1");
}

export function MenuButton() {
  const [open, setOpen] = useState(false);
  const requests = useGame(
    (s) => (s.view?.public.moreTimeRequests ?? 0) + (s.view?.public.pauseRequests ?? 0),
  );
  return (
    <>
      <button
        type="button"
        className="icon-btn menu-btn"
        aria-label={requests > 0 ? `Menu (${requests} requests)` : "Menu"}
        data-testid="menu-button"
        onClick={() => setOpen(true)}
      >
        ☰
        {requests > 0 ? (
          <span className="badge-count" data-testid="menu-requests">
            {requests}
          </span>
        ) : null}
      </button>
      <Sheet open={open} title="Menu" onClose={() => setOpen(false)} testId="menu">
        <MenuBody onClose={() => setOpen(false)} />
      </Sheet>
    </>
  );
}

function MenuBody({ onClose }: { onClose: () => void }) {
  const view = useView();
  const session = useGame((s) => s.session);
  const phase = view.public.phase;
  const inGame = phase.kind !== "lobby" && phase.kind !== "ended";
  const [large, setLarge] = useState(() => readLocal(LARGE_TEXT) === "1");
  const announcements = [...view.public.announcements].reverse().slice(0, 12);

  return (
    <div className="stack">
      <div className="row">
        <span className="grow muted">Rules</span>
        <RulesBadge rules={view.public.rules} />
      </div>
      <p className="center" style={{ margin: 0 }}>
        <RulesLink /> · <IntroLink />
      </p>
      {inGame && view.me.isHost ? <HostTimerControls onClose={onClose} /> : null}
      {inGame && !view.me.isHost ? (
        <div className="stack">
          <RequestCounts />
          <button
            type="button"
            className="btn block"
            data-testid="request-more-time"
            onClick={async () => {
              const ack = await send({ type: "requestMoreTime" });
              if (ack.ok) toast("The host sees that you want more time.");
              onClose();
            }}
          >
            Request more time
          </button>
          <button
            type="button"
            className="btn block"
            data-testid="request-pause"
            disabled={phase.paused}
            onClick={async () => {
              const ack = await send({ type: "requestPause" });
              if (ack.ok) toast("The host sees that you asked for a pause.");
              onClose();
            }}
          >
            Request pause
          </button>
        </div>
      ) : null}

      <label className="row panel" style={{ minHeight: "var(--tap)" }}>
        <input
          type="checkbox"
          checked={large}
          onChange={(e) => {
            setLarge(e.target.checked);
            writeLocal(LARGE_TEXT, e.target.checked ? "1" : null);
            applyTextSize();
          }}
          style={{ width: "1.4rem", height: "1.4rem" }}
        />
        <span>Large text</span>
      </label>

      {announcements.length > 0 ? (
        <section className="panel">
          <h3>Announcements</h3>
          <ul className="list">
            {announcements.map((a, i) => (
              <li key={`${a.at}-${i}`}>{a.text}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {phase.kind !== "ended" ? (
        <HoldButton
          className="btn danger block"
          onConfirm={() => {
            void send({ type: "leave" }).then((ack) => {
              if (!ack.ok || !session) return;
              forgetSession(session.code);
              navigate("/");
            });
          }}
        >
          {phase.kind === "lobby"
            ? "Hold to leave the room"
            : "Hold to leave the game (you become a Ghost)"}
        </HoldButton>
      ) : (
        <button type="button" className="btn block" onClick={() => navigate("/")}>
          Back to the start
        </button>
      )}
      <p className="faint center">The menu looks the same on every phone.</p>
    </div>
  );
}

/** "4 players want more time · 2 players asked for a pause" (public counts, §14.2–§14.3). */
export function RequestCounts() {
  const view = useView();
  const more = view.public.moreTimeRequests;
  const pause = view.public.pauseRequests;
  if (more === 0 && pause === 0) return null;
  const players = (n: number) => `${n} ${n === 1 ? "player" : "players"}`;
  return (
    <p className="request-counts" data-testid="request-counts">
      {more > 0 ? `${players(more)} ${more === 1 ? "wants" : "want"} more time` : null}
      {more > 0 && pause > 0 ? " · " : null}
      {pause > 0 ? `${players(pause)} asked for a pause` : null}
    </p>
  );
}

/** Host-only, in play. Every use is announced to everyone (§14.2). */
export function HostTimerControls({ onClose }: { onClose: () => void }) {
  const view = useView();
  const phase = view.public.phase;
  return (
    <section className="panel stack" data-testid="host-controls">
      <h3 style={{ margin: 0 }}>Host controls</h3>
      <p className="faint" style={{ margin: 0 }}>
        Every change is announced on every phone and the big screen.
      </p>
      <RequestCounts />
      {phase.paused ? (
        <button
          type="button"
          className="btn primary block"
          onClick={() => {
            void send({ type: "resume" });
            onClose();
          }}
        >
          Resume
        </button>
      ) : (
        <button
          type="button"
          className="btn block"
          data-testid="pause"
          onClick={() => {
            void send({ type: "pause" });
            onClose();
          }}
        >
          Pause
        </button>
      )}
      <InGameTimers phase={phase} timers={view.public.timers} onDone={onClose} />
      <ConnectScreenButton />
    </section>
  );
}
